import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';
import { styled } from '@compiled/react';

import Link from '@atlaskit/link';
import Avatar from '@atlaskit/avatar';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Flex, xcss } from '@atlaskit/primitives';
import SuitcaseIcon from '@atlaskit/icon/core/briefcase';
import EmailIcon from '@atlaskit/icon/core/email';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import { getAtlassianPeopleProfileUrl } from '@confluence/profile/entry-points/helpers.ts';
import { ProfileLink } from '@confluence/profile/entry-points/ProfileLinkComponent.ts';
import { ProfileAvatar } from '@confluence/profile/entry-points/ProfileAvatarComponent.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { AccessStatus } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';
import { ErrorDisplay } from '@confluence/error-boundary/entry-points/ErrorDisplay.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';
import type { ExtensionHandlerProps } from '@confluence/fabric-extension-lib/entry-points/commonExtensionTypes.ts';

import { i18n } from '../profileMacrosi18n.ts';
import { useProfileInfo } from '../useProfileInfo.tsx';

const userInfoContainerStyles = xcss({
	padding: 'space.150',
});

const iconContainerStyles = xcss({
	marginRight: 'space.050',
});

const namePlaceholderStyles = xcss({
	width: '180px',
	// @ts-ignore TS2322 - need this exact value or else size is too small/big
	padding: '10px',
	borderRadius: 'radius.small',
	backgroundColor: 'color.background.accent.gray.subtlest',
	marginBottom: 'space.025',
});

const jobTitlePlaceholderStyles = xcss({
	width: '200px',
	// @ts-ignore TS2322
	padding: '10px',
	borderRadius: 'radius.small',
	backgroundColor: 'color.background.accent.gray.subtlest',
	marginBottom: 'space.025',
});

const emailPlaceholderStyles = xcss({
	width: '230px',
	// @ts-ignore TS2322
	padding: '10px',
	borderRadius: 'radius.small',
	backgroundColor: 'color.background.accent.gray.subtlest',
	marginBottom: 'space.025',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const UserProfileContainer = styled.div({
	display: 'flex',
	padding: token('space.075'),
});

export type UserProfileMacroRendererProps = {
	macroDefaultProps: ExtensionHandlerProps;
	macroParams?: {
		user?: { value?: string };
	};
};

export const UserProfileMacroRendererComponent = ({
	macroDefaultProps,
	macroParams,
}: UserProfileMacroRendererProps) => {
	const { formatMessage } = useIntl();
	const { accessStatus } = useSessionData();
	const renderServerPlaceholder = useRenderServerPlaceholder();
	const isAnonymousUser = accessStatus === AccessStatus.ANONYMOUS_ACCESS;
	const userId = macroParams?.user?.value;

	const {
		hasViewUserProfilePermission,
		graphqlError,
		userName,
		iconUrl,
		email,
		jobTitle,
		isActiveUser,
	} = useProfileInfo(userId);

	const profileUrl = hasViewUserProfilePermission ? getAtlassianPeopleProfileUrl(userId) : '';
	const altText = `${formatMessage(i18n.imageAlt)}: ${userName}`;
	const emailTitle = `${formatMessage(i18n.emailTitle)} ${userName}`;
	const dataMacroParameters = {
		user: userId,
	};

	// If they don't have permissions at all for profile viewing, show the no access message.
	// The query to fetch user permissions is getting preloaded so all the placeholder code below will be fine to show.
	if (!hasViewUserProfilePermission) {
		return <FormattedMessage {...i18n.noAccess} />;
	}

	// Don't show anything during SSR if it's the config placeholder UI (no userId case)
	if (renderServerPlaceholder && !userId) {
		return null;
	}

	const getAvatar = () => {
		return renderServerPlaceholder ? (
			<Avatar appearance="circle" size="xlarge" testId="placeholder-avatar" />
		) : fg('dst-a11y__replace-anchor-with-link__page-experienc') ? (
			<Link data-testid="profile-link" href={profileUrl} target="_blank">
				<ProfileAvatar
					userId={userId}
					src={iconUrl}
					size="xlarge"
					name={altText}
					isAnonymous={isAnonymousUser}
					testId="profile-image"
				/>
			</Link>
		) : (
			// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
			<a data-testid="profile-link" href={profileUrl} target="_blank">
				<ProfileAvatar
					userId={userId}
					src={iconUrl}
					size="xlarge"
					name={altText}
					isAnonymous={isAnonymousUser}
					testId="profile-image"
				/>
			</a>
		);
	};

	const getUserNameLink = () => {
		// For deactivated users, show (Deactivated) next to their name
		const userNameText = isActiveUser
			? userName
			: formatMessage(i18n.deactivatedUser, { userFullName: userName });

		return renderServerPlaceholder ? (
			<Box xcss={namePlaceholderStyles} testId="name-placeholder" />
		) : (
			<span data-testid="user-profile-link">
				<ProfileLink userId={userId} fullName={userNameText} isAnonymous={isAnonymousUser} />
			</span>
		);
	};

	const getJobTitle = () => {
		return renderServerPlaceholder ? (
			<Box xcss={jobTitlePlaceholderStyles} testId="job-title-placeholder" />
		) : jobTitle ? (
			<Flex alignItems="center" testId="job-title-container">
				<Box xcss={iconContainerStyles}>
					<SuitcaseIcon label="" />
				</Box>
				{jobTitle}
			</Flex>
		) : (
			<div />
		);
	};

	const getEmail = () => {
		return renderServerPlaceholder && !isAnonymousUser ? (
			<Box xcss={emailPlaceholderStyles} testId="email-placeholder" />
		) : !isAnonymousUser && email ? (
			<Flex alignItems="center" testId="email-container">
				<Box xcss={iconContainerStyles}>
					<EmailIcon label="" />
				</Box>
				{fg('dst-a11y__replace-anchor-with-link__page-experienc') ? (
					<Link testId="user-email" title={emailTitle} href={`mailto:${email}`}>
						{email}
					</Link>
				) : (
					// eslint-disable-next-line @atlaskit/design-system/no-html-anchor
					<a data-testid="user-email" title={emailTitle} href={`mailto:${email}`}>
						{email}
					</a>
				)}
			</Flex>
		) : (
			<div />
		);
	};

	return (
		<UserProfileContainer
			data-fabric-macro={macroDefaultProps?.attributes?.macroId}
			data-macro-body=""
			data-macro-parameters={JSON.stringify(dataMacroParameters)}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
			className="profile-macro"
		>
			<div
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				className="userLogoLink"
				data-username={userId}
				data-account-id={userId}
			>
				{graphqlError && <ErrorDisplay error={graphqlError} />}
				{getAvatar()}
			</div>
			<Flex direction="column" xcss={userInfoContainerStyles}>
				{getUserNameLink()}
				{getJobTitle()}
				{getEmail()}
			</Flex>
		</UserProfileContainer>
	);
};

export const UserProfileMacroRenderer = (props: UserProfileMacroRendererProps) => {
	return (
		<GenericErrorBoundary
			renderOnError={() => <FormattedMessage {...i18n.infoError} />}
			attribution={Attribution.PAGE_EXTENSIONS}
		>
			<UserProfileMacroRendererComponent {...props} />
		</GenericErrorBoundary>
	);
};
